import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MoveRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import type { LocalPallet } from '../model/types';

interface PalletActionBarProps {
  selectedCount: number;
  hasSelectedInPallets: boolean;
  pallets: LocalPallet[];
  onMoveToPallet: (palletId: string) => void;
  onUnassign: () => void;
}

export function PalletActionBar({
  selectedCount,
  hasSelectedInPallets,
  pallets,
  onMoveToPallet,
  onUnassign
}: PalletActionBarProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  // Filter pallets based on search
  const filteredPallets = useMemo(() => {
    if (!search) return pallets;
    const searchLower = search.toLowerCase();
    return pallets.filter((p) => p.palletNumber.toLowerCase().includes(searchLower));
  }, [pallets, search]);

  if (selectedCount === 0) return null;

  return (
    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border">
      {hasSelectedInPallets && (
        <Button
          type="button"
          size="sm"
          variant="destructive"
          className="gap-1 whitespace-nowrap h-8"
          onClick={onUnassign}>
          {t('pallet.removeFromPallet', 'Remove from pallet')}
        </Button>
      )}

      {pallets.length > 0 && (
        <>
          <span className="text-sm font-medium whitespace-nowrap">
            {t('pallet.moveToPallet', 'Move {{count}} line(s) to:', { count: selectedCount })}
          </span>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('pallet.searchPallet', 'Search pallet...')}
            className="max-w-[200px] h-8"
          />
          <div className="flex items-center gap-1 overflow-x-auto">
            {filteredPallets.map((pallet) => (
              <Button
                key={pallet.id}
                type="button"
                size="sm"
                variant="outline"
                className="gap-1 whitespace-nowrap h-8"
                onClick={() => onMoveToPallet(pallet.id)}>
                <MoveRight className="h-3 w-3" />
                {pallet.palletNumber}
              </Button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
